/**
 * presentation-patch.ts
 *
 * Targeted patching of a saved GeneratedPresentation when the lesson's
 * instructional content changes.
 *
 * CONTRACT
 * ────────
 * • Only slides with a matching blueprintRef are updated.
 * • Unrelated slides, speaker notes, theme choices, images, and layout
 *   are left completely unchanged.
 * • Answer-reveal slides are patched by detecting the "Answer:" prefix and
 *   preserving it; when no prefix is found the heading is replaced with the
 *   new answer verbatim.
 * • Returns the original object unchanged when no diff is found.
 */

import { lessonContentKey, extractBlueprint, type LessonBlueprint } from './lesson-blueprint';
import type { GeneratedLesson } from './lesson-engine';
import type { GeneratedPresentation } from './presentation-engine';

// Blueprint section keys that can produce slides
const BLUEPRINT_SECTIONS = [
  'warmUp',
  'directInstruction',
  'guidedPractice',
  'independentPractice',
  'exitTicket',
  'challenge',
  'homework',
  'belowLevel',
  'onLevel',
  'aboveLevel',
] as const;

type SectionKey = typeof BLUEPRINT_SECTIONS[number];

interface ItemChange {
  studentContent: string;
  answer: string;
}

/** Keyed by "sectionKey:n" (n is 1-based). */
type ChangeMap = Map<string, ItemChange>;

function buildChangeMap(oldBlueprint: LessonBlueprint, newBlueprint: LessonBlueprint): ChangeMap {
  const changes: ChangeMap = new Map();

  for (const key of BLUEPRINT_SECTIONS) {
    const oldSection = oldBlueprint[key as SectionKey] as { items?: { n: number; studentContent: string; answer: string }[] } | undefined;
    const newSection = newBlueprint[key as SectionKey] as { items?: { n: number; studentContent: string; answer: string }[] } | undefined;
    if (!oldSection || !newSection) continue;

    const oldItems = oldSection.items ?? [];
    const newItems = newSection.items ?? [];

    for (const newItem of newItems) {
      const oldItem = oldItems.find(i => i.n === newItem.n);
      if (!oldItem) continue;
      if (
        oldItem.studentContent !== newItem.studentContent ||
        oldItem.answer !== newItem.answer
      ) {
        changes.set(`${key}:${newItem.n}`, {
          studentContent: newItem.studentContent,
          answer: newItem.answer,
        });
      }
    }
  }

  return changes;
}

function patchAnswerHeading(oldHeading: string, newAnswer: string): string {
  // Preserve "Answer:" prefix if present (case-insensitive), replace the rest
  const prefixMatch = oldHeading.match(/^(answer\s*:\s*)/i);
  if (prefixMatch) return prefixMatch[1] + newAnswer;
  return newAnswer;
}

/**
 * Returns a new GeneratedPresentation where any slide whose blueprintRef
 * matches a changed instructional item in the lesson has its content updated.
 *
 * Returns the original `presentation` reference when nothing changed.
 */
export function patchPresentationSlides(
  presentation: GeneratedPresentation,
  oldLesson: GeneratedLesson,
  newLesson: GeneratedLesson,
): GeneratedPresentation {
  // Fast path: if content keys are identical, nothing to patch
  if (lessonContentKey(oldLesson) === lessonContentKey(newLesson)) return presentation;

  const oldBlueprint = oldLesson.blueprint ?? extractBlueprint(oldLesson);
  const newBlueprint = newLesson.blueprint ?? extractBlueprint(newLesson);

  const changes = buildChangeMap(oldBlueprint, newBlueprint);
  if (changes.size === 0) return presentation;

  let anyChanged = false;
  const patchedSlides = presentation.slides.map(slide => {
    if (!slide.blueprintRef) return slide;
    const mapKey = `${slide.blueprintRef.section}:${slide.blueprintRef.n}`;
    const change = changes.get(mapKey);
    if (!change) return slide;

    anyChanged = true;
    if (slide.isAnswerSlide) {
      // Update both heading (display label) and bodyLines (the content shown on the slide)
      return {
        ...slide,
        heading:   patchAnswerHeading(slide.heading, change.answer),
        bodyLines: [`Answer: ${change.answer}`],
      };
    }
    // Content slide — update both heading (short label) and bodyLines (exact problem text)
    // so the student-facing body of the slide matches the new blueprint item exactly.
    return {
      ...slide,
      heading:   change.studentContent,
      bodyLines: change.studentContent.split('\n').filter(Boolean),
    };
  });

  if (!anyChanged) return presentation;

  return {
    ...presentation,
    // Update syncKey to reflect the new lesson state so the sync-warning
    // badge does not reappear immediately after patching.
    syncKey: lessonContentKey(newLesson),
    slides: patchedSlides,
  };
}
